import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.js';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import {
  ActionCard,
  ActionCardBody,
  ActionCardDivider,
  ActionCardFooter,
  ActionCardMeta,
  ActionCardSection,
  ActionCardSummary,
} from '@sola/ark-core/molecules/action-card/action-card.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import '@sola/locales/formatters.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';
import {
  getCommitmentLabel,
  getCommitmentTitle,
  getFrequencyAbbr,
} from '@sola/supporter-sdk/utils/commitments.js';

import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar.js';
import { CommitmentDetailsSheet } from '../../organisms/commitments-details-sheet/commitment-details-sheet';

export type CommitmentCardProps = {
  commitment: GraphQLCommitment;
  isActive?: boolean;
  onActiveChange?: (open: boolean, commitmentId: string) => void;
};

const useComponentData = (args: CommitmentCardProps & { t: TFunction }) => {
  const { commitment, t } = args;

  const icon = <CommitmentAvatar responsive commitment={commitment} />;
  const title = getCommitmentTitle(commitment, t);
  const label = getCommitmentLabel(commitment, t);

  const rate = t('numbers.currencyCustom', {
    amount: commitment?.rate,
    useNegativeParens: true,
  });

  return {
    commitment,
    isRecurring: commitment?.isRecurring,
    frequencyAbbr: commitment?.paymentFrequency
      ? getFrequencyAbbr(commitment.paymentFrequency, t)
      : undefined,
    icon,
    title,
    label,
    rate,
  };
};

/**
 * Commitment card, shown for both commitments and giving history items.
 */
export const CommitmentCard: React.FC<CommitmentCardProps> = (props) => {
  const { t } = useTranslation(['my', 'common']);

  const cd = useComponentData({ t, ...props });
  const { commitment, isActive, onActiveChange } = props;

  return (
    <ActionCard>
      <ActionCardSection>
        <ActionCardBody>
          <ActionCardSummary
            icon={cd.icon}
            title={cd.title}
            subTitle={cd.label}
          />
          <ActionCardMeta
            text={cd.rate}
            textContrast={cd.frequencyAbbr}
            badge={
              commitment.hasMissedPayment ? (
                <Badge
                  label={t('my:commitments.missedPaymentsLabel')}
                  color="negative"
                  responsive
                />
              ) : undefined
            }
          />
        </ActionCardBody>
      </ActionCardSection>

      {/* Footer */}
      <ActionCardDivider />
      <ActionCardFooter justify="between">
        <span
          className={cn(
            'flex items-center label-moderate label-neutral-mild label-sm md:label-md',
            {
              'label-warning': !cd.isRecurring,
            },
          )}
        >
          {!cd.isRecurring && (
            <>
              <ArkIcon
                iconName="alert-triangle"
                className="mr-snug-lg"
                size="1.25rem"
              />
              <span>{t('my:commitments.notRecurring.title')}</span>
            </>
          )}

          {cd.isRecurring &&
            t('my:commitments.nextPayment', {
              date: commitment.paymentDateNext,
            })}
        </span>

        <CommitmentDetailsSheet
          commitment={commitment!}
          isOpen={isActive}
          onOpenChange={onActiveChange}
          openTrigger={
            <div className="ml-auto">
              <LinkStyle
                noUnderline
                noPadding
                inline
                variant="primary"
                size="lg"
              >
                <button type="button" className="CommitmentCard__trigger">
                  {t('commitments.detailsSheet.triggerLabel')}
                </button>
              </LinkStyle>
            </div>
          }
        />
      </ActionCardFooter>
    </ActionCard>
  );
};
